import { useEffect, useState } from 'react';
import { eventsApi } from '../lib/api';
import { todayIso } from '@shared/event-date';
import type { Event } from '@shared/types';

interface Props {
  event: Event | null;     // null = create
  onClose: () => void;
  onSaved: () => void;
}

export default function EventEditModal({ event, onClose, onSaved }: Props) {
  const [name, setName] = useState(event?.name ?? '');
  const [description, setDescription] = useState(event?.description ?? '');
  const [longDescription, setLongDescription] = useState(event?.long_description ?? '');
  const [startDate, setStartDate] = useState(event?.startDate ?? todayIso());
  const [startTime, setStartTime] = useState(event?.startTime ?? '');
  const [endDate, setEndDate] = useState(event?.endDate ?? '');
  const [endTime, setEndTime] = useState(event?.endTime ?? '');
  const [linkLabel, setLinkLabel] = useState(event?.linkLabel ?? '');
  const [linkUrl, setLinkUrl] = useState(event?.linkUrl ?? '');
  const [hidden, setHidden] = useState(event?.hidden ?? false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isAllDay = !startTime;
  const isMultiDay = !!endDate;

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function toggleAllDay(next: boolean) {
    setStartTime(next ? '' : '19:00');
    if (next) setEndTime('');
  }

  function toggleMultiDay(next: boolean) {
    if (next) {
      setEndDate(startDate || todayIso());
    } else {
      setEndDate('');
      setEndTime('');
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setErr('Name is required');
      return;
    }
    setErr(null);
    setSubmitting(true);
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      long_description: longDescription.trim() || null,
      startDate,
      startTime: startTime || null,
      endDate: isMultiDay ? endDate || null : null,
      endTime: isMultiDay ? endTime || null : null,
      linkLabel: linkLabel.trim() || null,
      linkUrl: linkUrl.trim() || null,
      hidden,
    };
    try {
      if (event) {
        await eventsApi.update(event.id, { ...payload, expected_updated_at: event.updated_at });
      } else {
        await eventsApi.create(payload);
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl w-full max-w-xl my-4 sm:my-10 p-4 sm:p-6 space-y-4"
      >
        <h2 className="font-display text-xl text-slate-900">{event ? 'Edit event' : 'New event'}</h2>

        <FieldLabel>Name</FieldLabel>
        <input
          type="text"
          required
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Event name (e.g. The Garbologists Opening Night.)"
          className="input"
        />

        <FieldLabel>Description</FieldLabel>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="One-sentence description (used in the newsletter calendar)"
          rows={2}
          className="input resize-y"
        />

        <FieldLabel hint="Optional. Shown on the public events calendar; not used in the newsletter.">
          Longer description
        </FieldLabel>
        <textarea
          value={longDescription}
          onChange={(e) => setLongDescription(e.target.value)}
          placeholder="A paragraph or two with more context, optional"
          rows={5}
          className="input resize-y"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <FieldLabel>Start date</FieldLabel>
            <input
              type="date"
              required
              value={startDate}
              onChange={(e) => {
                const v = e.target.value;
                setStartDate(v);
                // Keep end date in sync if it's now before start.
                if (endDate && v && endDate < v) setEndDate(v);
              }}
              className="input"
            />
          </div>
          <div>
            <FieldLabel>Start time</FieldLabel>
            {isAllDay ? (
              <div className="input flex items-center text-slate-500 italic">All day</div>
            ) : (
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="input"
              />
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-2 pt-1">
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={isAllDay}
              onChange={(e) => toggleAllDay(e.target.checked)}
              className="h-4 w-4"
            />
            All-day event
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={isMultiDay}
              onChange={(e) => toggleMultiDay(e.target.checked)}
              className="h-4 w-4"
            />
            Multi-day event
          </label>
        </div>

        {isMultiDay && (
          <div>
            <FieldLabel>End date</FieldLabel>
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input"
            />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <FieldLabel>Link label</FieldLabel>
            <input
              type="text"
              value={linkLabel}
              onChange={(e) => setLinkLabel(e.target.value)}
              placeholder="e.g. Get tickets"
              className="input"
            />
          </div>
          <div>
            <FieldLabel>Link URL</FieldLabel>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://"
              className="input"
            />
          </div>
        </div>

        <label className="flex items-start gap-2 pt-2 text-sm text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            checked={hidden}
            onChange={(e) => setHidden(e.target.checked)}
            className="h-4 w-4 mt-0.5"
          />
          <span>
            <span className="font-medium">Hide this event</span>
            <span className="block text-xs text-slate-500 mt-0.5">
              Hidden events are excluded from the public calendar and the newsletter calendar block.
              Use to stage drafts or remove cancelled events without losing them.
            </span>
          </span>
        </label>

        {err && <p className="text-xs text-red-600">{err}</p>}

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
          <button type="submit" disabled={submitting} className="btn-primary flex-1 justify-center">
            {submitting ? 'Saving…' : event ? 'Save event' : 'Create event'}
          </button>
        </div>
      </form>
    </div>
  );
}

function FieldLabel({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide">
      {children}
      {hint && <span className="block normal-case font-normal tracking-normal text-slate-500 mt-0.5">{hint}</span>}
    </label>
  );
}
